/* ============================================
   Snap-scroll: smooth full-section scroll on mobile
   Targets sections listed in SNAP_SECTIONS array.
   Each section snaps to the next sibling on scroll down.
   Mobile only (< 990px). Desktop scrolls normally.
   ============================================ */

(() => {
  const SNAP_SECTIONS = [
    { selector: '.section-main-hero-wrapper', duration: null },
    { selector: '.section-home-shades-of-sukoon', duration: null },
    { selector: '.section-home-our-journey', duration: null },
  ];

  const mql = window.matchMedia('(min-width: 990px)');

  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

  const smoothScrollTo = (targetY, duration, lockedStartY) => {
    const startY = lockedStartY ?? window.scrollY;
    const distance = targetY - startY;
    let startTime = null;

    /* Lock to the captured position immediately so no micro-drift is visible */
    window.scrollTo(0, startY);

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + distance * easeInOutCubic(progress));

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        document.body.classList.remove('snap-scrolling');
      }
    };

    requestAnimationFrame(step);
  };

  const getDuration = (section, fallback) => {
    const el = section.querySelector('[data-snap-duration]');
    return el ? parseInt(el.dataset.snapDuration, 10) || fallback : fallback;
  };

  const isInSection = (section) => {
    const top = section.offsetTop;
    const bottom = top + section.offsetHeight;
    const scroll = window.scrollY;
    return scroll >= top - 10 && scroll < bottom - 10;
  };

  let animating = false;

  const init = () => {
    const entries = [];

    for (const cfg of SNAP_SECTIONS) {
      const el = document.querySelector(cfg.selector);
      if (!el) continue;
      const next = el.nextElementSibling;
      if (!next) continue;
      const dur = getDuration(el, cfg.duration || 600);
      entries.push({ section: el, next, duration: dur });
    }

    if (!entries.length) return;

    let touchStartY = 0;

    /* --- Wheel (trackpad / mouse) --- */
    window.addEventListener(
      'wheel',
      (e) => {
        if (mql.matches) return;

        /* Don't snap while a drawer/modal has locked the page scroll */
        if (document.body.classList.contains('overflow-hidden')) return;

        if (animating) {
          e.preventDefault();
          return;
        }

        for (const entry of entries) {
          if (isInSection(entry.section) && e.deltaY > 0) {
            e.preventDefault();
            animating = true;

            /* Capture scroll position NOW, before the browser can shift it */
            const lockedY = window.scrollY;
            document.body.classList.add('snap-scrolling');

            const targetY =
              entry.next.getBoundingClientRect().top + window.scrollY;
            smoothScrollTo(targetY, entry.duration, lockedY);
            setTimeout(() => {
              animating = false;
            }, entry.duration + 100);
            return;
          }
        }
      },
      { passive: false }
    );

    /* --- Block touchmove during animation to prevent scroll conflicts --- */
    window.addEventListener(
      'touchmove',
      (e) => {
        if (animating) e.preventDefault();
      },
      { passive: false }
    );

    /* --- Touch start --- */
    window.addEventListener(
      'touchstart',
      (e) => {
        touchStartY = e.touches[0].clientY;
      },
      { passive: true }
    );

    /* --- Touch end --- */
    window.addEventListener(
      'touchend',
      (e) => {
        if (mql.matches) return;
        if (animating) return;

        /* Don't snap while a drawer/modal has locked the page scroll */
        if (document.body.classList.contains('overflow-hidden')) return;

        const deltaY = touchStartY - e.changedTouches[0].clientY;
        if (deltaY <= 30) return;

        for (const entry of entries) {
          if (isInSection(entry.section)) {
            animating = true;
            document.body.classList.add('snap-scrolling');
            const targetY =
              entry.next.getBoundingClientRect().top + window.scrollY;
            smoothScrollTo(targetY, entry.duration);
            setTimeout(() => {
              animating = false;
            }, entry.duration + 100);
            return;
          }
        }
      },
      { passive: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
